import { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { RotateCw, ZoomIn, ZoomOut, X, Check, ImageIcon } from "lucide-react";

const OUTPUT_SIZE = 600;

/**
 * Core crop function — extracts exact pixel region from the source image.
 * Returns a high-quality JPEG blob of the cropped area scaled to OUTPUT_SIZE.
 */
async function cropImageToBlob(imageSrc, cropPixels, rotation = 0) {
  const img = await loadImage(imageSrc);

  // 1. Handle rotation: draw full image on a rotation-safe canvas
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const rw = Math.floor(img.width * cos + img.height * sin);
  const rh = Math.floor(img.width * sin + img.height * cos);

  const rotCanvas = document.createElement("canvas");
  rotCanvas.width = rw;
  rotCanvas.height = rh;
  const rCtx = rotCanvas.getContext("2d");
  rCtx.translate(rw / 2, rh / 2);
  rCtx.rotate(rad);
  rCtx.drawImage(img, -img.width / 2, -img.height / 2);

  // 2. Extract the exact crop region
  const sx = Math.max(0, Math.round(cropPixels.x));
  const sy = Math.max(0, Math.round(cropPixels.y));
  const sw = Math.round(cropPixels.width);
  const sh = Math.round(cropPixels.height);

  const outCanvas = document.createElement("canvas");
  outCanvas.width = OUTPUT_SIZE;
  outCanvas.height = OUTPUT_SIZE;
  const oCtx = outCanvas.getContext("2d");
  oCtx.imageSmoothingEnabled = true;
  oCtx.imageSmoothingQuality = "high";
  oCtx.drawImage(rotCanvas, sx, sy, sw, sh, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  return new Promise((resolve, reject) => {
    outCanvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob returned null"))),
      "image/jpeg",
      0.92
    );
  });
}

/** Convert a file/dataURL to an image-ready blob for direct upload (no crop). */
async function imageToBlob(imageSrc) {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob returned null"))),
      "image/jpeg",
      0.92
    );
  });
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
}

export default function ImageCropper({ open, onClose, imageSrc, onCropComplete, aspectRatio = 1 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const cropperRef = useRef(null);

  const onCropDone = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const handleSaveCropped = async () => {
    if (!croppedPixels || !imageSrc) return;
    setSaving(true);
    try {
      const blob = await cropImageToBlob(imageSrc, croppedPixels, rotation);
      onCropComplete(blob);
      doClose();
    } catch (e) {
      console.error("Crop save error:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOriginal = async () => {
    if (!imageSrc) return;
    setSaving(true);
    try {
      const blob = await imageToBlob(imageSrc);
      onCropComplete(blob);
      doClose();
    } catch (e) {
      console.error("Original save error:", e);
    } finally {
      setSaving(false);
    }
  };

  const doClose = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedPixels(null);
    onClose();
  };

  if (!open || !imageSrc) return null;

  const zoomPct = Math.round((zoom - 1) * 100);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="image-cropper-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" onClick={doClose} />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-lg mx-3 overflow-hidden rounded-2xl"
        style={{
          background: "linear-gradient(180deg, #0d0d12 0%, #0a0a0f 100%)",
          border: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 32px 64px rgba(0,0,0,0.7)",
        }}
        data-testid="image-cropper-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06]">
          <button onClick={doClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-all" data-testid="crop-cancel-btn">
            <X className="w-5 h-5" />
          </button>
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-white">
            Crop Photo
          </h2>
          <button onClick={handleSaveCropped} disabled={saving || !croppedPixels} className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#00BFFF] text-white hover:bg-[#00BFFF]/80 transition-all disabled:opacity-40" data-testid="crop-confirm-btn">
            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-5 h-5" />}
          </button>
        </div>

        {/* Crop Canvas */}
        <div className="relative w-full bg-black" style={{ height: "min(65vw, 400px)" }} data-testid="crop-area">
          {/* WhatsApp-style blurred background */}
          {imageSrc && (
            <div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `url(${imageSrc})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(30px) brightness(0.3) saturate(1.2)",
              }}
            />
          )}
          <Cropper
            ref={cropperRef}
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            cropShape="round"
            showGrid={false}
            objectFit="auto-cover"
            restrictPosition={true}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropDone}
            minZoom={1}
            maxZoom={5}
            style={{
              containerStyle: { background: "transparent" },
              cropAreaStyle: {
                border: "2.5px solid rgba(0,191,255,0.6)",
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.6), 0 0 24px rgba(0,191,255,0.12)",
              },
            }}
          />
        </div>

        {/* Zoom Controls */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <button onClick={() => setZoom(z => Math.max(1, +(z - 0.1).toFixed(2)))} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/[0.06] transition-all" data-testid="zoom-out-btn">
              <ZoomOut className="w-4 h-4" />
            </button>
            <div className="flex-1">
              <Slider value={[zoom]} min={1} max={5} step={0.05} onValueChange={v => setZoom(v[0])} data-testid="zoom-slider" />
            </div>
            <button onClick={() => setZoom(z => Math.min(5, +(z + 0.1).toFixed(2)))} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/[0.06] transition-all" data-testid="zoom-in-btn">
              <ZoomIn className="w-4 h-4" />
            </button>
            <span className="text-[11px] text-zinc-500 font-mono w-12 text-right tabular-nums" data-testid="zoom-percent">
              {zoomPct >= 0 ? `+${zoomPct}` : zoomPct}%
            </span>
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex items-center justify-center gap-2 px-5 pb-3">
          <button onClick={() => setRotation(r => (r + 90) % 360)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-zinc-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-all" data-testid="rotate-btn">
            <RotateCw className="w-3.5 h-3.5" /> Rotate
          </button>
          <button onClick={() => { setCrop({x:0,y:0}); setZoom(1); setRotation(0); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-zinc-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-all" data-testid="reset-btn">
            Reset
          </button>
        </div>

        {/* Footer — Save Cropped or Use Original */}
        <div className="flex gap-3 px-5 pb-5 pt-1">
          <Button
            variant="outline"
            onClick={handleSaveOriginal}
            disabled={saving}
            className="flex-1 h-12 rounded-xl bg-white/[0.03] border-white/[0.08] text-zinc-300 hover:bg-white/[0.06] hover:text-white font-semibold text-xs uppercase tracking-widest transition-all gap-2"
            data-testid="save-original-btn"
          >
            <ImageIcon className="w-4 h-4" />
            Use Original
          </Button>
          <Button
            onClick={handleSaveCropped}
            disabled={saving || !croppedPixels}
            className="flex-1 h-12 rounded-xl font-semibold text-xs uppercase tracking-widest transition-all disabled:opacity-40"
            style={{
              background: saving ? "rgba(0,191,255,0.15)" : "linear-gradient(135deg, #00BFFF 0%, #0080FF 100%)",
              color: "#fff",
              boxShadow: saving ? "none" : "0 4px 20px rgba(0,191,255,0.25), inset 0 1px 0 rgba(255,255,255,0.1)",
            }}
            data-testid="crop-save-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              "Save Crop"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
